/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeBreakpointContext,
  BaSeGrid,
  BaSeGridItem,
  BaSeRadio,
} from '@base/react';
import React from 'react';
import { useCandidateController } from '../../../../hooks/controllers/candidate-controller';
import { formatCpf } from '../../../../utils/formatters/cpfMask';
import { phoneMask } from '../../../../utils/formatters/phoneMask';
import { useInterviewContext } from '../../context/interview-context';
import { useValidationsContext } from '../../context/validations-context';
import CommonCheckbox from '../common-checkbox/common-checkbox';
import CommonInput from '../common-input/common-input';
import {
  Container,
  Content,
  DescriptionHeader,
  RadioWrapper,
  Separator,
  TitleContent,
} from './form-candidate-style';


export const FormCandidate = () => {
  const breakpointController = React.useContext(BaSeBreakpointContext);
  const { getProfessionalSituation } = useCandidateController();
  const { interview, formData, handleChangeFormData, radioId, setRadioId } =
    useInterviewContext();

  const {
    formErrors,
    setFormErrors,
    setAceptEmancipated,
    aceptEmancipated,
    isUnderage,
  } = useValidationsContext();

  const [situations, setSituations] = React.useState([]);

  const isLargeScreen =
    breakpointController.inDesktop ||
    breakpointController.inDesktopLarge ||
    breakpointController.inTablet;

  const size = isLargeScreen ? 6 : 12;
  const sizeuf = isLargeScreen ? 4 : 12;
  const sizecity = isLargeScreen ? 8 : 12;

  React.useEffect(() => {
    if (!interview?.module?.id) return;

    getProfessionalSituation(interview.module.id)
      .then((response) => {
        setSituations(response);
      })
      .catch(() => {
        setSituations([]);
      });
  }, [interview.module.id, getProfessionalSituation]);

  const handleBlur = React.useCallback(
    (fieldName, value, rules) => {
      if (rules.required && (!value || value.trim() === '')) {
        setFormErrors((prev) => ({
          ...prev,
          [fieldName]: 'Este campo é obrigatório',
        }));
        return;
      }

      if (rules.phone && value) {
        const cleanPhone = value.replace(/\D/g, '');
        if (cleanPhone.length < 10) {
          setFormErrors((prev) => ({
            ...prev,
            [fieldName]: 'Telefone inválido',
          }));
          return;
        }
      }

      if (rules.maxDate && value) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const selectedDate = new Date(value);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
          setFormErrors((prev) => ({
            ...prev,
            [fieldName]: rules.maxDateMessage || 'Data não pode ser futura',
          }));
          return;
        }
      }

      if (rules.minAge && value) {
        const today = new Date();
        const birthDate = new Date(value);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        const actualAge =
          monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
            ? age - 1
            : age;

        if (actualAge < rules.minAge) {
          setFormErrors((prev) => ({
            ...prev,
            [fieldName]:
              rules.minAgeMessage || `Idade mínima: ${rules.minAge} anos`,
          }));
          return;
        }
      }

      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    },
    [setFormErrors],
  );

  const handleFieldChange = React.useCallback(
    (e) => {
      handleChangeFormData(e);
      const fieldName = e.target.name;
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    },
    [handleChangeFormData, setFormErrors],
  );

  return (
    <Container>
      <DescriptionHeader>
        Precisamos confirmar algumas informações para personalizar sua
        experiência.
      </DescriptionHeader>

      <Content>
        <BaSeGrid gap={8}>
          {/*ESSE CAMPO VAI PASSAR POR REFINAMENTO  */}
          {/* <BaSeGridItem size={12}>
            <CommonInput
              label="Nome social"
              placeholder="Nome social"
              name="socialName"
              value={formData?.socialName}
              onChange={handleFieldChange}
            />
          </BaSeGridItem> */}

          <BaSeGridItem size={12}>
            <CommonInput
              label="Nome completo"
              placeholder="Nome completo"
              required
              disabled
              value={interview?.candidate.name}
              name="fullName"
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              label="CPF"
              placeholder="000.000.000-00"
              name="cpf"
              mask="cpf"
              required
              disabled
              value={formatCpf(interview?.candidate.cpf)}
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              required
              label="E-mail"
              placeholder="fulano@email.com"
              name="email"
              disabled
              value={interview?.email}
            />
          </BaSeGridItem>

          <BaSeGridItem size={sizeuf}>
            <CommonInput
              required
              label="UF do seminário"
              placeholder="UF"
              name="ufSeminar"
              disabled
              value={interview?.city.uf.initials}
            />
          </BaSeGridItem>

          <BaSeGridItem size={sizecity}>
            <CommonInput
              required
              label="Cidade do seminário"
              placeholder="Cidade do seminário *"
              name="city"
              value={interview?.city.name}
              disabled
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              label="Data de nascimento"
              placeholder="dd/mm/aaa"
              value={formData.birthDate}
              onChange={handleFieldChange}
              onBlur={(e) => {
                if (e.target.validationError) {
                  setFormErrors((prev) => ({
                    ...prev,
                    birthDate: e.target.validationError,
                  }));
                  return;
                }

                handleBlur('birthDate', e.target.value, { required: true });
              }}
              name="birthDate"
              type="text"
              mask="date"
              validateDate={true}
              required
              error={formErrors?.birthDate}
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              required
              label="Telefone"
              placeholder="(00) 00000-0000"
              name="phone"
              mask="phone"
              value={phoneMask(formData.phone)}
              onChange={handleFieldChange}
              onBlur={(e) =>
                handleBlur('phone', e.target.value, {
                  required: true,
                  phone: true,
                })
              }
              error={formErrors?.phone}
            />
          </BaSeGridItem>

          {isUnderage(formData.birthDate) && (
            <BaSeGridItem size={12}>
              <div style={{ display: 'flex' }}>
                <CommonCheckbox
                  name="emancipated"
                  checked={aceptEmancipated}
                  onChange={(isChecked) => {
                    setAceptEmancipated(isChecked);
                  }}
                />
                <p>
                  Confirmo que sou menor de idade e fui emancipado pelos meus
                  pais.
                </p>
              </div>
              {!aceptEmancipated && formErrors?.emancipated && (
                <p
                  style={{
                    color: '#f73450',
                    fontSize: '0.875rem',
                    marginTop: '0.25rem',
                    display: 'block',
                  }}
                >
                  {formErrors.emancipated}
                </p>
              )}
            </BaSeGridItem>
          )}

          <BaSeGridItem size={12}>
            <Separator />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <TitleContent>Informe um contato de emergência</TitleContent>
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              label="Nome completo"
              placeholder="Nome completo"
              name="emergencyContactName"
              value={formData.emergencyContactName}
              onChange={handleFieldChange}
              onBlur={(e) =>
                handleBlur('emergencyContactName', e.target.value, {
                  required: true,
                })
              }
              required
              error={formErrors?.emergencyContactName}
            />
          </BaSeGridItem>

          <BaSeGridItem size={size}>
            <CommonInput
              required
              label="Telefone"
              placeholder="(00) 0000-0000"
              name="emergencyContactPhone"
              value={phoneMask(formData.emergencyContactPhone)}
              onChange={handleFieldChange}
              onBlur={(e) =>
                handleBlur('emergencyContactPhone', e.target.value, {
                  required: true,
                  phone: true,
                })
              }
              mask="phone"
              error={formErrors?.emergencyContactPhone}
            />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <Separator />
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <TitleContent>
              Sobre a sua situação profissional, você pode dizer que atualmente:
            </TitleContent>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <RadioWrapper>
              {situations.map((it) => (
                <BaSeRadio
                  key={it.id}
                  checked={it.id === radioId}
                  color="default"
                  id={it.id}
                  label={it.description}
                  name={it.description}
                  onChange={() => {
                    setRadioId(it.id);
                    // Limpa erro de situação profissional
                    setFormErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.professionalSituation;
                      return newErrors;
                    });
                  }}
                  size="small"
                />
              ))}
            </RadioWrapper>
            {formErrors?.professionalSituation && (
              <p
                style={{
                  color: '#f73450',
                  fontSize: '0.875rem',
                  marginTop: '0.25rem',
                  display: 'block',
                }}
              >
                {formErrors.professionalSituation}
              </p>
            )}
          </BaSeGridItem>
        </BaSeGrid>
      </Content>
    </Container>
  );
};
